import React, { FC, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import moment, { Moment } from 'moment-timezone';
import { useSelector } from 'react-redux';

import { ICreateAbsenceRequestDTO, IPreferencesDTO, IScheduleStatusDTO } from 'src/api/dtos';
import { InfoFilledIcon, MoreHorizontalIcon, SingleDatePicker, TimePicker, Tooltip } from 'src/shared/components';
import { API_DATE_FORMAT, DATE_TIME_FORMAT } from 'src/shared/constants';
import { TimeFormatEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { getScheduleStatusDescription } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';

import './PartialAbsenceInput.styles.scss';

type InputsType = Partial<Pick<ICreateAbsenceRequestDTO, 'startDate' | 'endDate' | 'endTime' | 'startTime'>>;

interface Props {
  inputs: InputsType;
  inputTargetKey: keyof ICreateAbsenceRequestDTO | undefined;
  scheduledWarnings: IScheduleStatusDTO[];
  preferences: IPreferencesDTO | null;
  onUpdate: (inputs: InputsType) => void;
}

export const PartialAbsenceInputs: FC<Props> = ({
  inputs,
  inputTargetKey,
  scheduledWarnings,
  preferences,
  onUpdate,
}) => {
  const { t } = useTranslate();
  const { startDate, startTime, endTime } = inputs;
  const timeFormat = useSelector(getTimeFormat);

  const [startTimeFocused, setStartTimeFocused] = useState<boolean>(false);
  const [endTimeFocused, setEndTimeFocused] = useState<boolean>(false);

  useEffect(() => {
    if (!inputTargetKey) {
      return;
    }

    switch (inputTargetKey) {
      case 'endTime':
        setEndTimeFocused(true);
        break;
      case 'startDate':
      case 'startTime':
      default:
        setStartTimeFocused(true);
    }
  }, [inputTargetKey]);

  const handleDateChange = (date: Moment | null) => {
    const newInputs: InputsType = {
      ...inputs,
      startDate: date?.format(API_DATE_FORMAT),
      endDate: date?.format(API_DATE_FORMAT),
    };

    setStartTimeFocused(false);
    onUpdate(newInputs);
  };

  const handleStartTimeChange = (date: Date | null) => {
    const newStartTime = date ? moment(date).format(DATE_TIME_FORMAT) : undefined;
    const newEndTime =
      !endTime || moment(date).isSameOrAfter(endTime) ? undefined : moment(endTime).format(DATE_TIME_FORMAT);

    const newInputs: InputsType = { ...inputs, startTime: newStartTime, endTime: newEndTime };

    setStartTimeFocused(false);
    onUpdate(newInputs);
  };

  const handleEndTimeChange = (date: Date | null) => {
    const newEndTime = date ? moment(date).format(DATE_TIME_FORMAT) : undefined;

    const newInputs: InputsType = { ...inputs, endTime: newEndTime };

    setEndTimeFocused(false);
    onUpdate(newInputs);
  };

  return (
    <Stack position="relative" direction="row" justifyContent="flex-start" gap={1} marginBottom={2}>
      <Box sx={{ minWidth: '242px' }}>
        <label className="small mb-2 fw-medium d-block required">{t('absences.selectDay')}</label>
        <SingleDatePicker
          id="date"
          value={startDate ? moment(startDate) : null}
          onChange={handleDateChange}
          placeholder={t('absences.selectDate')}
        />
      </Box>
      <Stack direction="row" justifyContent="center">
        <TimePicker
          ampm={preferences?.timeFormat === TimeFormatEnums.T_12_HOURS}
          focused={startTimeFocused}
          inputLabel={t('global.startTime')}
          value={startTime ? moment(startTime) : null}
          onAccept={handleStartTimeChange}
          onClear={() => handleStartTimeChange(null)}
        />
        <TimePicker
          ampm={preferences?.timeFormat === TimeFormatEnums.T_12_HOURS}
          focused={endTimeFocused}
          minTime={startTime ? moment(startTime) : undefined}
          inputLabel={t('global.endTime')}
          value={endTime ? moment(endTime) : null}
          onAccept={handleEndTimeChange}
          onClear={() => handleEndTimeChange(null)}
        />
      </Stack>
      {!!scheduledWarnings.length && (
        <Stack spacing={0.5} direction="row" className="partial-status-warning" alignItems="center">
          <Stack direction="row" spacing={0.5} className="text-warning">
            <InfoFilledIcon size={16} />
            <Typography fontSize="0.75rem">{getScheduleStatusDescription(scheduledWarnings[0], timeFormat)}</Typography>
          </Stack>

          {scheduledWarnings.length > 1 && (
            <Tooltip
              title={
                <div>
                  {scheduledWarnings.map((warning, index) => (
                    <div key={`schedule-warning-info-${index}`}>
                      {getScheduleStatusDescription(warning, timeFormat)}
                    </div>
                  ))}
                </div>
              }
            >
              <IconButton size="small" color="warning">
                <MoreHorizontalIcon size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Stack>
  );
};
